import { useEffect, useRef, useState } from "react";
import { Tag, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";

const TAG_LABELS = {
  senior: "Senior",
  pwd:    "PWD",
};
const TAG_TONE = "bg-amber-50 text-amber-700 border-amber-200";

export default function SeniorPwdTagControl({
  patient,
  canEdit,
  onChanged,
  testIdPrefix = "senior-pwd",
}) {
  const tag = patient?.senior_pwd_tag ?? null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function pick(next) {
    if (busy || next === tag) { setOpen(false); return; }
    setBusy(true);
    const { error } = await supabase.rpc("set_patient_senior_pwd_tag", {
      p_patient_id: patient.id,
      p_tag:        next, // null clears
    });
    setBusy(false);
    setOpen(false);
    if (error) { toast.error(error.message ?? "Could not update tag."); return; }
    toast.success(next ? `Tagged as ${TAG_LABELS[next]}` : "Tag removed");
    onChanged?.();
  }

  if (!tag && !canEdit) return null;

  return (
    <div className="relative inline-flex" ref={wrapRef}>
      {tag ? (
        <button
          type="button"
          onClick={canEdit ? () => setOpen((v) => !v) : undefined}
          disabled={!canEdit}
          title={canEdit ? "Click to change" : `${TAG_LABELS[tag]} (PH 20% discount eligible)`}
          data-testid={`${testIdPrefix}-tag-pill`}
          className={`text-[11px] uppercase tracking-wider font-semibold px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 ${TAG_TONE} ${canEdit ? "hover:opacity-80" : "cursor-default"}`}
        >
          <Tag className="w-3 h-3" /> {TAG_LABELS[tag]}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testIdPrefix}-tag-add`}
          className="text-[11px] font-medium px-2.5 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400 inline-flex items-center gap-1"
        >
          <Tag className="w-3 h-3" /> Tag Senior / PWD
        </button>
      )}

      {open ? (
        <div
          role="menu"
          data-testid={`${testIdPrefix}-tag-menu`}
          className="absolute top-full left-0 mt-1 z-50 bg-white rounded-xl shadow-lg border border-slate-200 py-1 min-w-[180px]"
        >
          <MenuRow label="Senior Citizen" sub="20% (PH law)" active={tag === "senior"} disabled={busy} onClick={() => pick("senior")} testId={`${testIdPrefix}-tag-pick-senior`} />
          <MenuRow label="PWD"            sub="20% (PH law)" active={tag === "pwd"}    disabled={busy} onClick={() => pick("pwd")}    testId={`${testIdPrefix}-tag-pick-pwd`} />
          <div className="my-1 border-t border-slate-100" />
          <MenuRow label="None"           sub="No legal discount"           active={!tag}            disabled={busy} onClick={() => pick(null)}     testId={`${testIdPrefix}-tag-pick-none`} subtle />
        </div>
      ) : null}
    </div>
  );
}

function MenuRow({ label, sub, active, disabled, onClick, testId, subtle = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 ${subtle ? "text-slate-600" : "text-slate-900"} disabled:opacity-50`}
    >
      <span className="flex flex-col">
        <span className="font-semibold">{label}</span>
        <span className="text-[11px] text-slate-500">{sub}</span>
      </span>
      {active ? <Check className="w-4 h-4 text-teal-600 shrink-0" /> : null}
    </button>
  );
}
